/**
 * S3 Storage Driver
 *
 * Almacena archivos en AWS S3 o compatible (MinIO, DigitalOcean Spaces, etc.)
 */

import { Readable } from 'node:stream'
import { createHash } from 'node:crypto'
import { extname, basename, dirname } from 'node:path'
import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  DeleteObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
  CopyObjectCommand
} from '@aws-sdk/client-s3'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'
import type { StorageDriver, StorageFile, StorageFileInfo, PutOptions, GetOptions } from './driver.interface.js'
import type { StorageConfig } from '../storage.config.js'

export class S3Driver implements StorageDriver {
  private client: S3Client
  private bucket: string
  private baseUrl: string | undefined
  private generateId: () => string

  constructor(config: StorageConfig) {
    if (!config.s3) {
      throw new Error('S3 configuration is required for S3Driver')
    }

    const { bucket, region, accessKeyId, secretAccessKey, endpoint } = config.s3

    this.bucket = bucket
    this.baseUrl = config.baseUrl
    this.generateId = config.generateId

    this.client = new S3Client({
      region,
      credentials: {
        accessKeyId,
        secretAccessKey
      },
      ...(endpoint && {
        endpoint,
        forcePathStyle: true // Necesario para MinIO
      })
    })
  }

  async put(buffer: Buffer, filename: string, options?: PutOptions): Promise<StorageFile> {
    const id = this.generateId()
    const ext = extname(filename) || ''
    const diskFilename = `${id}${ext}`
    const folder = options?.folder || ''
    const key = folder ? `${folder}/${diskFilename}` : diskFilename

    // Calcular hash SHA256
    const hash = createHash('sha256').update(buffer).digest('hex')

    // Subir a S3
    await this.client.send(new PutObjectCommand({
      Bucket: this.bucket,
      Key: key,
      Body: buffer,
      ContentType: options?.mimetype || 'application/octet-stream',
      ContentLength: buffer.length,
      Metadata: {
        'original-filename': filename,
        'sha256': hash
      }
    }))

    return {
      id,
      filename,
      diskFilename,
      mimetype: options?.mimetype || 'application/octet-stream',
      size: buffer.length,
      folder: folder || undefined,
      path: key,
      url: this.getUrl(key) || undefined,
      hash
    }
  }

  async get(path: string, options?: GetOptions): Promise<NodeJS.ReadableStream> {
    const response = await this.client.send(new GetObjectCommand({
      Bucket: this.bucket,
      Key: path,
      ...(options?.start !== undefined || options?.end !== undefined
        ? { Range: `bytes=${options?.start ?? 0}-${options?.end ?? ''}` }
        : {})
    }))

    if (!response.Body) {
      throw new Error(`File not found: ${path}`)
    }

    // El Body de S3 es un Readable stream
    return response.Body as Readable
  }

  async getBuffer(path: string): Promise<Buffer> {
    const response = await this.client.send(new GetObjectCommand({
      Bucket: this.bucket,
      Key: path
    }))

    if (!response.Body) {
      throw new Error(`File not found: ${path}`)
    }

    // AWS SDK v3: transformToByteArray handles all stream types uniformly
    const bytes = await response.Body.transformToByteArray()
    return Buffer.from(bytes)
  }

  async delete(path: string): Promise<void> {
    await this.client.send(new DeleteObjectCommand({
      Bucket: this.bucket,
      Key: path
    }))
  }

  async exists(path: string): Promise<boolean> {
    try {
      await this.client.send(new HeadObjectCommand({
        Bucket: this.bucket,
        Key: path
      }))
      return true
    } catch {
      return false
    }
  }

  getUrl(path: string): string | null {
    if (this.baseUrl) {
      return `${this.baseUrl}/files/${path}`
    }
    // URL pública de S3 (si el bucket tiene permisos públicos)
    return `https://${this.bucket}.s3.amazonaws.com/${path}`
  }

  // ============================================================================
  // EXTENDED METHODS
  // ============================================================================

  async list(folder?: string): Promise<StorageFileInfo[]> {
    const prefix = folder ? (folder.endsWith('/') ? folder : `${folder}/`) : ''

    const response = await this.client.send(new ListObjectsV2Command({
      Bucket: this.bucket,
      Prefix: prefix,
      Delimiter: '/'
    }))

    const results: StorageFileInfo[] = []

    // Archivos
    if (response.Contents) {
      for (const obj of response.Contents) {
        if (!obj.Key || obj.Key === prefix) continue

        results.push({
          path: obj.Key,
          filename: basename(obj.Key),
          size: obj.Size || 0,
          lastModified: obj.LastModified,
          isDirectory: false
        })
      }
    }

    // Carpetas (prefijos comunes)
    if (response.CommonPrefixes) {
      for (const prefix of response.CommonPrefixes) {
        if (!prefix.Prefix) continue

        const folderPath = prefix.Prefix.endsWith('/')
          ? prefix.Prefix.slice(0, -1)
          : prefix.Prefix

        results.push({
          path: folderPath,
          filename: basename(folderPath),
          size: 0,
          isDirectory: true
        })
      }
    }

    return results
  }

  async copy(src: string, dst: string): Promise<StorageFile> {
    // S3 CopyObject hace la copia server-side (eficiente)
    await this.client.send(new CopyObjectCommand({
      Bucket: this.bucket,
      CopySource: `${this.bucket}/${src}`,
      Key: dst
    }))

    // Obtener metadata del archivo copiado
    const headResponse = await this.client.send(new HeadObjectCommand({
      Bucket: this.bucket,
      Key: dst
    }))

    const id = this.generateId()
    const filename = basename(dst)
    const folder = dirname(dst) === '.' ? undefined : dirname(dst)

    return {
      id,
      filename,
      diskFilename: filename,
      mimetype: headResponse.ContentType || 'application/octet-stream',
      size: headResponse.ContentLength || 0,
      folder,
      path: dst,
      url: this.getUrl(dst) || undefined,
      hash: headResponse.Metadata?.['sha256']
    }
  }

  async move(src: string, dst: string): Promise<StorageFile> {
    // S3 no tiene move nativo: copy + delete
    const result = await this.copy(src, dst)
    await this.delete(src)
    return result
  }

  async getMetadata(path: string): Promise<StorageFileInfo> {
    const response = await this.client.send(new HeadObjectCommand({
      Bucket: this.bucket,
      Key: path
    }))

    return {
      path,
      filename: basename(path),
      size: response.ContentLength || 0,
      mimetype: response.ContentType,
      lastModified: response.LastModified,
      isDirectory: false
    }
  }

  async getSignedUrl(path: string, expiresInSeconds: number): Promise<string> {
    const command = new GetObjectCommand({
      Bucket: this.bucket,
      Key: path
    })

    return getSignedUrl(this.client, command, { expiresIn: expiresInSeconds })
  }

  async createFolder(path: string): Promise<void> {
    // En S3 las carpetas son prefijos, pero podemos crear un marker object
    const folderKey = path.endsWith('/') ? path : `${path}/`

    await this.client.send(new PutObjectCommand({
      Bucket: this.bucket,
      Key: folderKey,
      Body: '',
      ContentLength: 0
    }))
  }
}
